import React, { useState, useMemo } from "react"
import { Link } from "react-router-dom"
import {
  Rocket,
  Gamepad2,
  Waves,
  ChefHat,
  Building2,
  Music,
  Leaf,
  Code,
  Microscope,
  Palette,
  Zap,
  Globe,
} from "lucide-react"
import { ExplorerHeader } from "../components/ui/career-explorer/explorer-header"
import { ExplorerSearchBar } from "../components/ui/career-explorer/explorer-search-bar"
import { ExplorerFilterTabs } from "../components/ui/career-explorer/explorer-filter-tabs"
import { Sidebar } from "../components/ui/sidebar"
import { UniversalSearch } from "../components/ui/universal-search"
import {AIChatBox} from "../components/ui/ai-chat-box";
import ProtectedRoute from "../components/ProtectedRoute"

const allCareers = [
  { id: "1", title: "Astronaut", description: "Explore the stars, conduct research, and travel beyond Earth.", icon: Rocket, iconBgColor: "bg-red-100", iconColor: "text-red-500", category: "Popular Careers" },
  { id: "2", title: "Game Designer", description: "Create amazing worlds, characters, and stories for video games.", icon: Gamepad2, iconBgColor: "bg-purple-100", iconColor: "text-purple-500", category: "Popular Careers" },
  { id: "3", title: "Marine Biologist", description: "Study ocean life, protect marine ecosystems, and discover new species.", icon: Waves, iconBgColor: "bg-teal-100", iconColor: "text-teal-600", category: "Popular Careers" },
  { id: "4", title: "Chef", description: "Invent delicious recipes, cook amazing food, and run a kitchen.", icon: ChefHat, iconBgColor: "bg-orange-100", iconColor: "text-orange-500", category: "Popular Careers" },
  { id: "5", title: "Architect", description: "Design buildings and spaces where people live, work, and play.", icon: Building2, iconBgColor: "bg-sky-100", iconColor: "text-sky-500", category: "Popular Careers" },
  { id: "6", title: "Musician", description: "Create and perform music, write songs, and inspire with sound.", icon: Music, iconBgColor: "bg-pink-100", iconColor: "text-pink-500", category: "Popular Careers" },
  { id: "7", title: "Environmental Scientist", description: "Protect our planet by studying the environment and finding solutions.", icon: Leaf, iconBgColor: "bg-green-100", iconColor: "text-green-500", category: "Popular Careers" },
  { id: "8", title: "Software Developer", description: "Build apps, websites, and software that solve real-world problems.", icon: Code, iconBgColor: "bg-indigo-100", iconColor: "text-indigo-500", category: "Popular Careers" },
  { id: "9", title: "Data Scientist", description: "Analyze data, find patterns, and make informed decisions.", icon: Microscope, iconBgColor: "bg-blue-100", iconColor: "text-blue-500", category: "New Modules" },
  { id: "10", title: "Graphic Designer", description: "Create visual designs, logos, and artwork.", icon: Palette, iconBgColor: "bg-rose-100", iconColor: "text-rose-500", category: "New Modules" },
  { id: "11", title: "Electrical Engineer", description: "Design and build electrical systems and power solutions.", icon: Zap, iconBgColor: "bg-yellow-100", iconColor: "text-yellow-500", category: "New Modules" },
  { id: "12", title: "Travel Blogger", description: "Explore the world, share experiences, and inspire others.", icon: Globe, iconBgColor: "bg-cyan-100", iconColor: "text-cyan-600", category: "New Modules" },
]

export default function CareerExplorer() {
  const [activeTab, setActiveTab] = useState("Popular Careers")
  const [searchQuery, setSearchQuery] = useState("")
  const [myCareers, setMyCareers] = useState([])
  const [showNotification, setShowNotification] = useState(false)
  const [addedCareerTitle, setAddedCareerTitle] = useState("")

  // Filter careers
  const filteredCareers = useMemo(() => {
    return allCareers.filter((career) => {
      const matchesTab = career.category === activeTab
      const matchesSearch =
        career.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        career.description.toLowerCase().includes(searchQuery.toLowerCase())
      return matchesTab && matchesSearch
    })
  }, [activeTab, searchQuery])

  const handleViewCareerPath = (careerId) => {
    console.log(`View career path for: ${careerId}`)
  }

  const handleAddToMyCareer = (careerId, careerTitle) => {
    if (!myCareers.includes(careerId)) {
      setMyCareers([...myCareers, careerId])
      setAddedCareerTitle(careerTitle)
      setShowNotification(true)
      
      // Hide notification after 3 seconds
      setTimeout(() => {
        setShowNotification(false)
      }, 3000)
      
      console.log(`Added career ${careerId}`)
    }
  }

  const handleSearch = (query) => {
    console.log("Universal search for:", query)
  }

  return (
   <ProtectedRoute>
     <div className="flex min-h-screen bg-slate-200">
      {/* Notification Popup */}
      {showNotification && (
        <div className="fixed top-4 right-4 z-50 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg animate-in slide-in-from-right duration-300">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">✓ Added to your career path!</span>
          </div>
          <p className="text-xs opacity-90 mt-1">{addedCareerTitle} has been added to your career journey</p>
        </div>
      )}

      {/* Sidebar - Self-contained */}
      <Sidebar />

      <AIChatBox />

      {/* Universal Search Component */}
      <UniversalSearch onSearch={handleSearch} />

      {/* Main content */}
      <main className="flex-1 lg:ml-64 p-3 sm:p-4 md:p-6 lg:p-8 transition-all duration-300 ease-in-out">
        {/* Mobile Header */}
        <div className="md:hidden flex items-center gap-3 mb-4 pb-4 border-b border-slate-200">
          <div className="flex-1">
            <h1 className="text-lg font-bold text-slate-900">
              Career Explorer
            </h1>
            <p className="text-sm text-slate-600">
              Discover your future career
            </p>
          </div>
        </div>

        {/* Desktop Header */}
        <div className="hidden md:block">
          <ExplorerHeader />
        </div>

        {/* Search */}
        <div className="mt-4 sm:mt-6 md:mt-8">
          <ExplorerSearchBar value={searchQuery} onChange={setSearchQuery} />
        </div>

        {/* Filter Tabs (mobile: 3-column grid layout) */}
        <div className="mt-4 sm:mt-6">
          <div className="hidden sm:block">
            <ExplorerFilterTabs activeTab={activeTab} onTabChange={setActiveTab} />
          </div>

          {/* Mobile 3-column layout */}
          <div className="grid grid-cols-3 gap-2 sm:hidden">
            {["Popular Careers", "New Modules", "Quizzes"].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`text-xs font-medium py-2 px-1 rounded-md transition-colors ${
                  activeTab === tab
                    ? "bg-blue-600 text-white"
                    : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                }`}
              >
                {tab === "Popular Careers"
                  ? "Career Paths"
                  : tab === "New Modules"
                  ? "My Learning Journey"
                  : "Challenges"}
              </button>
            ))}
          </div>
        </div>

        {/* Careers Grid */}
        <section className="mt-4 sm:mt-6 md:mt-8">
          <h3 className="text-xl sm:text-2xl text-gray-700 font-bold mb-4 sm:mb-6">
            {activeTab === "Popular Careers" && "Personalized For You"}
          </h3>

          {filteredCareers.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
              {filteredCareers.map((career) => (
                <div
                  key={career.id}
                  className="bg-slate-100 rounded-xl shadow-sm border border-slate-200 p-4 sm:p-6 hover:shadow-md transition-shadow"
                >
                  <div
                    className={`${career.iconBgColor} rounded-lg w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center mb-3 sm:mb-4`}
                  >
                    <career.icon
                      className={`w-5 h-5 sm:w-6 sm:h-6 ${career.iconColor}`}
                    />
                  </div>
                  <h4 className="text-base sm:text-lg font-semibold text-slate-800 mb-2 line-clamp-1">
                    {career.title}
                  </h4>
                  <p className="text-slate-600 text-xs sm:text-sm mb-4 line-clamp-2">
                    {career.description}
                  </p>
                  <div className="flex flex-col space-y-2">
                    <Link to={`/career-learning-path?id=${career.id}`}>
                      <button
                        onClick={() => handleViewCareerPath(career.id)}
                        className="w-full bg-blue-500 hover:bg-blue-600 text-white py-2 px-3 sm:px-4 rounded-lg text-xs sm:text-sm font-medium transition-colors"
                      >
                        View Career Path
                      </button>
                    </Link>
                    <button
                      onClick={() => handleAddToMyCareer(career.id, career.title)}
                      className="w-full border border-slate-300 hover:bg-slate-50 text-slate-700 py-2 px-3 sm:px-4 rounded-lg text-xs sm:text-sm font-medium transition-colors"
                    >
                      Add to my Career
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 sm:py-12">
              <p className="text-slate-600 text-base sm:text-lg">
                No careers found matching your search.
              </p>
            </div>
          )}
        </section>
      </main>
    </div>
   </ProtectedRoute>
  )
}